import { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  NumberInput,
  NumberInputField,
  VStack,
} from '@chakra-ui/react'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'
import { useQueryClient } from '@tanstack/react-query'
import { AVAILABILITY_STATUSES_QUERY_KEY } from '@/lib/userAvailability'

export type AgentAvailabilityStatusFormProps = {
  statusId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

const initialValues = {
  code: '',
  label: '',
  pauses_sla: true,
  routable: false,
  duration_minutes: '' as string | number,
  color: '#6B7280',
  sort_order: 0,
  is_active: true,
  show_in_header: true,
  show_in_agent_status: true,
  is_legacy: false,
}

export function AgentAvailabilityStatusForm({
  statusId,
  onSuccess,
  onCancel,
}: AgentAvailabilityStatusFormProps) {
  const { showError, showSuccess } = useToastNotification()
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(!!statusId)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [form, setForm] = useState(initialValues)
  const [protectedCode, setProtectedCode] = useState(false)

  const loadOne = useCallback(async () => {
    if (!statusId) return
    setLoading(true)
    setErrors({})
    try {
      const { data } = await apiClient.get(`/service-catalog/agent-availability-statuses/${statusId}`)
      const r = data?.data ?? data
      const code = r.code ?? ''
      setProtectedCode(code === 'online' || code === 'offline')
      setForm({
        code,
        label: r.label ?? '',
        pauses_sla: r.pauses_sla === true,
        routable: r.routable === true,
        duration_minutes: r.duration_minutes ?? '',
        color: r.color ?? '#6B7280',
        sort_order: Number(r.sort_order ?? 0),
        is_active: r.is_active !== false,
        show_in_header: r.show_in_header === true,
        show_in_agent_status: r.show_in_agent_status === true,
        is_legacy: r.is_legacy === true,
      })
    } catch (e: unknown) {
      showError(e as Error)
    } finally {
      setLoading(false)
    }
  }, [statusId, showError])

  useEffect(() => {
    if (statusId) loadOne()
    else {
      setForm(initialValues)
      setProtectedCode(false)
    }
  }, [statusId, loadOne])

  const updateField = (field: keyof typeof form, value: string | boolean | number) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setSaving(true)
    try {
      const duration =
        form.duration_minutes === '' || form.duration_minutes === null
          ? null
          : Number(form.duration_minutes)
      const payload = {
        code: form.code.trim(),
        label: form.label.trim(),
        pauses_sla: form.pauses_sla,
        routable: form.routable,
        duration_minutes: Number.isFinite(duration as number) ? duration : null,
        color: form.color.trim() || null,
        sort_order: Number(form.sort_order) || 0,
        is_active: form.is_active,
        show_in_header: form.show_in_header,
        show_in_agent_status: form.show_in_agent_status,
        is_legacy: form.is_legacy,
      }
      if (statusId) {
        await apiClient.put(`/service-catalog/agent-availability-statuses/${statusId}`, payload)
        showSuccess('Success', 'Availability status updated successfully')
      } else {
        await apiClient.post('/service-catalog/agent-availability-statuses', payload)
        showSuccess('Success', 'Availability status created successfully')
      }
      await queryClient.invalidateQueries({ queryKey: AVAILABILITY_STATUSES_QUERY_KEY })
      onSuccess?.()
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response
        ?.data
      if (res?.errors && typeof res.errors === 'object') setErrors(res.errors)
      else showError(err as Error)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Box py={8}>Loading...</Box>

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Code *
          </FormLabel>
          <FormControl isInvalid={!!errors.code?.length} isRequired>
            <Input
              value={form.code}
              onChange={(e) => updateField('code', e.target.value)}
              placeholder="e.g. break_15_1"
              isDisabled={!!statusId}
            />
            {!!statusId && (
              <Box as="span" fontSize="sm" color="gray.500">
                Code cannot be changed after creation.
              </Box>
            )}
            <FormErrorMessage>{errors.code?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Label *
          </FormLabel>
          <FormControl isInvalid={!!errors.label?.length} isRequired>
            <Input
              value={form.label}
              onChange={(e) => updateField('label', e.target.value)}
              placeholder="e.g. 1st 15mins Break"
            />
            <FormErrorMessage>{errors.label?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Color
          </FormLabel>
          <FormControl isInvalid={!!errors.color?.length}>
            <InputGroup>
              <InputLeftElement pointerEvents="none" h="100%">
                <Box
                  w="16px"
                  h="16px"
                  borderRadius="sm"
                  bg={form.color || '#6B7280'}
                  border="1px solid"
                  borderColor="gray.300"
                />
              </InputLeftElement>
              <Input
                value={form.color}
                onChange={(e) => updateField('color', e.target.value)}
                placeholder="#22C55E"
                pl={10}
              />
            </InputGroup>
            <FormErrorMessage>{errors.color?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Duration (minutes)
          </FormLabel>
          <FormControl isInvalid={!!errors.duration_minutes?.length}>
            <NumberInput
              min={1}
              max={1440}
              value={form.duration_minutes}
              onChange={(valueString) => updateField('duration_minutes', valueString)}
            >
              <NumberInputField placeholder="Leave empty for manual return" />
            </NumberInput>
            <FormErrorMessage>{errors.duration_minutes?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Sort order
          </FormLabel>
          <FormControl isInvalid={!!errors.sort_order?.length}>
            <NumberInput
              value={form.sort_order}
              onChange={(_, valueNumber) =>
                updateField('sort_order', Number.isNaN(valueNumber) ? 0 : valueNumber)
              }
            >
              <NumberInputField />
            </NumberInput>
            <FormErrorMessage>{errors.sort_order?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
          <FormLabel pt={2} fontWeight="medium" mb={0}>
            Flags
          </FormLabel>
          <VStack align="stretch" spacing={2}>
            <Checkbox
              isChecked={form.pauses_sla}
              onChange={(e) => updateField('pauses_sla', e.target.checked)}
              colorScheme="blue"
            >
              Pauses SLA clocks
            </Checkbox>
            <Checkbox
              isChecked={form.routable}
              onChange={(e) => updateField('routable', e.target.checked)}
              colorScheme="blue"
            >
              Routable (eligible for auto-assignment)
            </Checkbox>
            <Checkbox
              isChecked={form.show_in_header}
              onChange={(e) => updateField('show_in_header', e.target.checked)}
              colorScheme="blue"
            >
              Show in header User Status menu
            </Checkbox>
            <Checkbox
              isChecked={form.show_in_agent_status}
              onChange={(e) => updateField('show_in_agent_status', e.target.checked)}
              colorScheme="blue"
            >
              Show on Agent Status page
            </Checkbox>
            <Checkbox
              isChecked={form.is_active}
              onChange={(e) => updateField('is_active', e.target.checked)}
              colorScheme="blue"
              isDisabled={protectedCode}
            >
              Active
            </Checkbox>
            <Checkbox
              isChecked={form.is_legacy}
              onChange={(e) => updateField('is_legacy', e.target.checked)}
              colorScheme="blue"
            >
              Legacy (accepted for validation, hidden from pickers)
            </Checkbox>
          </VStack>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} isDisabled={saving}>
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={saving}
            loadingText={statusId ? 'Saving...' : 'Creating...'}
          >
            {statusId ? 'Save' : 'Create Status'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
